import CloseIcon from '@mui/icons-material/Close';
import InstallMobileIcon from '@mui/icons-material/InstallMobile';
import { Box, Button, IconButton, Paper, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  isAppInstalled,
  isBeforeInstallPromptEvent,
  type BeforeInstallPromptEvent,
} from '@/pwa/installPrompt';

export function PwaInstallBanner() {
  const { t } = useTranslation();
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(() => isAppInstalled());
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    function handleBeforeInstallPrompt(event: Event) {
      if (!isBeforeInstallPromptEvent(event) || isAppInstalled()) {
        return;
      }

      event.preventDefault();
      setInstallPrompt(event);
      setIsDismissed(false);
    }

    function handleInstalled() {
      setIsInstalled(true);
      setInstallPrompt(null);
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  if (isInstalled || isDismissed || installPrompt === null) {
    return null;
  }

  async function handleInstall() {
    if (!installPrompt) return;

    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;

    if (choice.outcome === 'accepted') {
      setIsInstalled(true);
    }

    setInstallPrompt(null);
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        right: { xs: 12, sm: 20 },
        bottom: { xs: 12, sm: 20 },
        left: { xs: 12, sm: 'auto' },
        zIndex: (theme) => theme.zIndex.snackbar - 1,
        '@media print': { display: 'none' },
      }}
    >
      <Paper
        elevation={8}
        sx={{
          width: { xs: '100%', sm: 440 },
          border: 1,
          borderColor: 'divider',
          p: 1.5,
        }}
      >
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
          <InstallMobileIcon color="primary" />
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Install Nang Book
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add the app to this device for faster access.
            </Typography>
          </Box>
          <Button variant="contained" size="small" onClick={() => void handleInstall()}>
            Install now
          </Button>
          <IconButton aria-label={t('shell.dismissInstallBanner')} size="small" onClick={() => setIsDismissed(true)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Paper>
    </Box>
  );
}
